import { useState } from "react";
import { useParams } from "react-router-dom";
import { LoadingButton } from "@mui/lab";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import useDataset from "datasets/hooks/useDataset";
import { removeDashboard } from "datasets/pages/dashboards/api/dashboardApi";
import useDatasetDashboards from "datasets/pages/dashboards/hooks/useDatasetDashboards";
import Dashboard from "datasets/pages/dashboards/types/interfaces/dashboard";
import { getApiErrorMessage, isApiError } from "~/utils/apiClient";
import rumClient, { trackEvent } from "~/utils/monitoring";

const RemoveReportModal = ({
  open,
  onClose,
  report,
}: {
  open: boolean;
  onClose: () => void;
  report: Dashboard;
}) => {
  const { datasetId } = useParams();
  const { mutate: mutateDataset } = useDataset();
  const { mutate: mutateDashboards } = useDatasetDashboards();
  const [error, setError] = useState("");
  const [removing, setRemoving] = useState(false);

  const handleRemove = async () => {
    setError("");
    setRemoving(true);

    try {
      await removeDashboard(datasetId!, report.id);

      trackEvent("dataset_dashboard_removed", {
        dataset_id: datasetId,
        dashboard_id: report.id,
      });

      await mutateDataset();
      await mutateDashboards();
      onClose();
    } catch (e: any) {
      console.error(e);
      rumClient?.recordError(e);
      setError((isApiError(e) && getApiErrorMessage(e)) || e.message);
    }

    setRemoving(false);
  };

  return (
    <Dialog fullWidth open={open} onClose={onClose}>
      <DialogTitle>Remove &quot;{report.displayName}&quot;</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error">{error}</Alert>}
        Are you sure you want to remove this dashboard?
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          loading={removing}
          onClick={handleRemove}
          sx={{
            bgcolor: "attention.main",
            "&:hover": { bgcolor: "attention.dark" },
          }}
        >
          Remove
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default RemoveReportModal;
